// src/pages/team/GameStatsPage.js
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { db, auth } from "../../firebase";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  updateDoc
} from "firebase/firestore";
import { useAuthState } from "react-firebase-hooks/auth";

function GameStatsPage() {
  const { teamId } = useParams();
  const [user] = useAuthState(auth);
  const [canManageGames, setCanManageGames] = useState(false);
  const [games, setGames] = useState([]);
  const [selectedGameId, setSelectedGameId] = useState("");
  const [yourScore, setYourScore] = useState("");
  const [opponentScore, setOpponentScore] = useState("");
  const [teamName, setTeamName] = useState("Team");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // -------------------
  // Enhanced Color Scheme & Style Objects
  // -------------------
  const colors = {
    primary: "#1E40AF", // Deeper blue
    accent: "#3B82F6", // Brighter blue
    background: "#F3F4F6", // Light gray background
    card: "#FFFFFF",
    text: "#1F2937", // Dark gray text
    textSecondary: "#6B7280", // Medium gray text
    border: "#E5E7EB", // Light border
    danger: "#DC2626", // Red for errors
    success: "#10B981", // Green for success
    button: {
      primary: "#2563EB",
      hover: "#1D4ED8"
    }
  };

  // Improved styles with modern aesthetic
  const pageStyles = {
    container: {
      backgroundColor: colors.background,
      minHeight: "100vh",
      padding: "40px 20px",
      display: "flex",
      justifyContent: "center",
      alignItems: "flex-start"
    },
    card: {
      backgroundColor: colors.card,
      borderRadius: "16px",
      padding: "32px",
      width: "100%",
      maxWidth: "650px",
      boxShadow: "0 10px 25px rgba(0, 0, 0, 0.05)",
      border: `1px solid ${colors.border}`
    },
    header: {
      color: colors.primary,
      fontSize: "28px",
      fontWeight: "700",
      textAlign: "center",
      marginBottom: "24px",
      padding: "0 0 16px 0",
      borderBottom: `2px solid ${colors.border}`
    },
    subHeader: {
      color: colors.text,
      fontSize: "20px",
      fontWeight: "600",
      marginBottom: "20px",
      marginTop: "16px",
      textAlign: "left"
    },
    form: {
      display: "flex",
      flexDirection: "column",
      gap: "20px"
    },
    formGroup: {
      display: "flex",
      flexDirection: "column",
      marginBottom: "4px"
    },
    label: {
      fontSize: "15px",
      color: colors.text,
      marginBottom: "8px",
      fontWeight: "500"
    },
    input: {
      padding: "12px 16px",
      borderRadius: "8px",
      border: `1px solid ${colors.border}`,
      fontSize: "16px",
      width: "100%",
      backgroundColor: "#F9FAFB",
      transition: "border-color 0.3s, box-shadow 0.3s",
      outline: "none"
    },
    select: {
      padding: "12px 16px",
      borderRadius: "8px",
      border: `1px solid ${colors.border}`,
      fontSize: "16px",
      width: "100%",
      backgroundColor: "#F9FAFB",
      outline: "none",
      appearance: "none",
      backgroundImage: "url('data:image/svg+xml;utf8,<svg fill=\"%236B7280\" viewBox=\"0 0 20 20\" xmlns=\"http://www.w3.org/2000/svg\"><path d=\"M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z\"></path></svg>')",
      backgroundRepeat: "no-repeat",
      backgroundPosition: "right 12px center",
      backgroundSize: "20px 20px"
    },
    button: {
      backgroundColor: colors.button.primary,
      color: "#fff",
      border: "none",
      borderRadius: "8px",
      padding: "14px 20px",
      fontSize: "16px",
      fontWeight: "600",
      cursor: "pointer",
      transition: "all 0.2s ease",
      marginTop: "16px",
      textAlign: "center",
      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)"
    },
    scoreInputContainer: {
      display: "flex",
      gap: "16px",
      alignItems: "center",
      justifyContent: "space-between"
    },
    scoreColumn: {
      flex: 1
    },
    scoreLabel: {
      display: "flex",
      alignItems: "center",
      gap: "8px"
    },
    infoText: {
      textAlign: "center",
      color: colors.textSecondary,
      marginBottom: "24px",
      padding: "12px 16px",
      backgroundColor: "#F9FAFB",
      borderRadius: "8px",
      border: `1px solid ${colors.border}`,
      fontSize: "15px",
      lineHeight: "1.5"
    },
    loaderContainer: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh"
    },
    loader: {
      border: "4px solid #f3f3f3",
      borderTop: `4px solid ${colors.accent}`,
      borderRadius: "50%",
      width: "40px",
      height: "40px",
      animation: "spin 1s linear infinite"
    },
    errorText: {
      color: colors.danger,
      textAlign: "center",
      padding: "16px",
      backgroundColor: "#FEF2F2",
      borderRadius: "8px",
      border: "1px solid #FECACA",
      margin: "20px 0"
    },
    gameListSection: {
      marginTop: "32px"
    },
    gameListTitle: {
      color: colors.text,
      fontSize: "20px",
      fontWeight: "600",
      marginBottom: "16px",
      borderBottom: `1px solid ${colors.border}`,
      paddingBottom: "8px"
    },
    gameList: {
      listStyle: "none",
      padding: 0,
      margin: 0
    },
    gameItem: {
      padding: "12px 16px",
      borderRadius: "8px",
      backgroundColor: "#F9FAFB",
      marginBottom: "8px",
      border: `1px solid ${colors.border}`,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center"
    },
    gameDateAndOpponent: {
      fontWeight: "500",
      color: colors.text
    },
    gameScore: {
      fontWeight: "700",
      color: colors.primary
    },
    statusBadge: {
      padding: "4px 8px",
      borderRadius: "4px",
      fontSize: "12px",
      fontWeight: "600",
      textTransform: "uppercase"
    },
    statusCompleted: {
      backgroundColor: "#ECFDF5",
      color: "#065F46",
      border: "1px solid #A7F3D0"
    },
    statusPending: {
      backgroundColor: "#FEF3C7",
      color: "#92400E",
      border: "1px solid #FDE68A"
    }
  };

  // Add custom hover effect to button
  const buttonHoverStyle = {
    ...pageStyles.button,
    backgroundColor: colors.button.hover,
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)"
  };

  // Create a hover state for the button
  const [isButtonHovered, setIsButtonHovered] = useState(false);

  // -------------------
  // Data Fetching
  // -------------------

  // Fetch team name for header
  useEffect(() => {
    const fetchTeamName = async () => {
      try {
        const teamRef = doc(db, "teams", teamId);
        const teamSnap = await getDoc(teamRef);
        if (teamSnap.exists()) {
          const data = teamSnap.data();
          setTeamName(data.name || "Team");
        }
      } catch (err) {
        console.error("Error fetching team name:", err);
      }
    };
    fetchTeamName();
  }, [teamId]);

  // Check manager status (using coachId)
  useEffect(() => {
    const checkManagerStatus = async () => {
      if (!user) return;
      try {
        const teamRef = doc(db, "teams", teamId);
        const teamSnap = await getDoc(teamRef);
        if (teamSnap.exists() && teamSnap.data().coachId === user.uid) {
          setCanManageGames(true);
        }
      } catch (err) {
        console.error("Error checking manager status:", err);
        setCanManageGames(false);
      }
    };
    checkManagerStatus();
  }, [user, teamId]);

  // Fetch games from Firestore - handles both old and new data models
  useEffect(() => {
    const fetchGames = async () => {
      setLoading(true);
      setError(null);
      try {
        // Try to fetch games using the new data model first
        const newModelQuery = query(
          collection(db, "games"),
          where("teamId", "==", teamId)
        );
        
        const newModelSnap = await getDocs(newModelQuery);
        let gamesData = newModelSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // If no games found with new model, try the old model
        if (gamesData.length === 0) {
          const homeQuery = query(
            collection(db, "games"),
            where("homeTeamId", "==", teamId)
          );
          const awayQuery = query(
            collection(db, "games"),
            where("awayTeamId", "==", teamId)
          );
          
          const [homeSnap, awaySnap] = await Promise.all([
            getDocs(homeQuery),
            getDocs(awayQuery)
          ]);

          const homeGames = homeSnap.docs.map((doc) => {
            const data = doc.data();
            // Convert old model to new model
            return {
              id: doc.id,
              ...data,
              yourScore: data.homeScore,
              opponentScore: data.awayScore,
              teamId: teamId,
              // Keep original fields for compatibility
              isHomeTeam: true
            };
          });
          
          const awayGames = awaySnap.docs.map((doc) => {
            const data = doc.data();
            // Convert old model to new model
            return {
              id: doc.id,
              ...data,
              yourScore: data.awayScore,
              opponentScore: data.homeScore,
              teamId: teamId,
              // Keep original fields for compatibility
              isHomeTeam: false
            };
          });
          
          gamesData = [...homeGames, ...awayGames];
        }

        // Sort games by date (most recent first)
        const sortedGames = gamesData.sort((a, b) => {
          return new Date(b.date) - new Date(a.date);
        });

        setGames(sortedGames);
      } catch (err) {
        console.error("Error fetching games:", err);
        setError("Failed to fetch games. Please try again later.");
      }
      setLoading(false);
    };
    
    fetchGames();
  }, [teamId]);

  // Handle game selection - automatically fill in existing scores if available
  const handleGameSelection = (e) => {
    const gameId = e.target.value;
    setSelectedGameId(gameId);
    
    if (gameId) {
      const selectedGame = games.find(game => game.id === gameId);
      if (selectedGame) {
        // Check for scores in the new model first
        if (selectedGame.yourScore !== undefined) {
          setYourScore(selectedGame.yourScore.toString());
          setOpponentScore(selectedGame.opponentScore.toString());
        } 
        // Fall back to old model if needed
        else if (selectedGame.homeScore !== undefined) {
          if (selectedGame.isHomeTeam) {
            setYourScore(selectedGame.homeScore.toString());
            setOpponentScore(selectedGame.awayScore.toString());
          } else {
            setYourScore(selectedGame.awayScore.toString());
            setOpponentScore(selectedGame.homeScore.toString());
          }
        } else {
          setYourScore("");
          setOpponentScore("");
        }
      }
    } else {
      setYourScore("");
      setOpponentScore("");
    }
  };

  // Handle updating the score - supports both data models
  const handleUpdateScore = async (e) => {
    e.preventDefault();
    if (!canManageGames) {
      alert("You are not authorized to update game scores.");
      return;
    }
    if (!selectedGameId) {
      alert("Please select a game first.");
      return;
    }
    
    try {
      const selectedGame = games.find(game => game.id === selectedGameId);
      const gameRef = doc(db, "games", selectedGameId);
      
      // Prepare update data based on the game's data model
      let updateData = {
        status: "completed"
      };
      
      // New data model
      if (selectedGame.teamId) {
        updateData.yourScore = parseInt(yourScore || "0");
        updateData.opponentScore = parseInt(opponentScore || "0");
      } 
      // Old data model
      else {
        if (selectedGame.isHomeTeam) {
          updateData.homeScore = parseInt(yourScore || "0");
          updateData.awayScore = parseInt(opponentScore || "0");
        } else {
          updateData.homeScore = parseInt(opponentScore || "0");
          updateData.awayScore = parseInt(yourScore || "0");
        }
      }
      
      await updateDoc(gameRef, updateData);
      
      // Update local state to reflect changes
      setGames(games.map(game => {
        if (game.id === selectedGameId) {
          // Create updated game object with appropriate score fields
          const updatedGame = { ...game, status: "completed" };
          
          // Update scores based on data model
          if (game.teamId) {
            updatedGame.yourScore = parseInt(yourScore || "0");
            updatedGame.opponentScore = parseInt(opponentScore || "0");
          } else {
            if (game.isHomeTeam) {
              updatedGame.homeScore = parseInt(yourScore || "0");
              updatedGame.awayScore = parseInt(opponentScore || "0");
            } else {
              updatedGame.homeScore = parseInt(opponentScore || "0");
              updatedGame.awayScore = parseInt(yourScore || "0");
            }
            
            // Also add new model fields for UI consistency
            updatedGame.yourScore = parseInt(yourScore || "0");
            updatedGame.opponentScore = parseInt(opponentScore || "0");
          }
          
          return updatedGame;
        }
        return game;
      }));
      
      alert("Game score updated successfully!");
      
      // Clear the form
      setSelectedGameId("");
      setYourScore("");
      setOpponentScore("");
    } catch (err) {
      console.error("Error updating game score:", err);
      alert("Failed to update game score. Please try again.");
    }
  };

  // Format date for display
  const formatGameDate = (dateString) => {
    const options = { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Determine appropriate score to display
  const getScoreDisplay = (game) => {
    // New data model
    if (game.yourScore !== undefined) {
      return {
        yourScore: game.yourScore,
        opponentScore: game.opponentScore,
        hasScore: true
      };
    }
    // Old data model
    else if (game.homeScore !== undefined) {
      return {
        yourScore: game.isHomeTeam ? game.homeScore : game.awayScore,
        opponentScore: game.isHomeTeam ? game.awayScore : game.homeScore,
        hasScore: true
      };
    }
    
    return {
      yourScore: 0,
      opponentScore: 0,
      hasScore: false
    };
  };

  if (loading) {
    return (
      <div style={pageStyles.loaderContainer}>
        <div style={pageStyles.loader}></div>
      </div>
    );
  }

  if (error) {
    return <div style={pageStyles.container}>
      <div style={pageStyles.card}>
        <h2 style={pageStyles.header}>{teamName} Games</h2>
        <div style={pageStyles.errorText}>{error}</div>
      </div>
    </div>;
  }

  return (
    <div style={pageStyles.container}>
      <div style={pageStyles.card}>
        <h2 style={pageStyles.header}>{teamName} Game Statistics</h2>
        
        {!canManageGames ? (
          <div style={pageStyles.infoText}>
            Only the team manager can update game scores. Viewing game statistics only.
          </div>
        ) : (
          <>
            <h3 style={pageStyles.subHeader}>Update Game Score</h3>
            <form onSubmit={handleUpdateScore} style={pageStyles.form}>
              <div style={pageStyles.formGroup}>
                <label style={pageStyles.label}>Select Game:</label>
                <select
                  value={selectedGameId}
                  onChange={handleGameSelection}
                  required
                  style={pageStyles.select}
                >
                  <option value="">-- Select a game --</option>
                  {games.map((game) => (
                    <option key={game.id} value={game.id}>
                      {formatGameDate(game.date)} vs {game.opponentName || "Opponent"}
                    </option>
                  ))}
                </select>
              </div>
              
              <div style={pageStyles.scoreInputContainer}>
                <div style={pageStyles.scoreColumn}>
                  <label style={pageStyles.label}>
                    <span style={pageStyles.scoreLabel}>
                      <span>Your Score</span>
                    </span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={yourScore}
                    onChange={(e) => setYourScore(e.target.value)}
                    placeholder="0"
                    style={pageStyles.input}
                  />
                </div>
                
                <div style={pageStyles.scoreColumn}>
                  <label style={pageStyles.label}>
                    <span style={pageStyles.scoreLabel}>
                      <span>Opponent Score</span>
                    </span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={opponentScore}
                    onChange={(e) => setOpponentScore(e.target.value)}
                    placeholder="0"
                    style={pageStyles.input}
                  />
                </div>
              </div>
              
              <button 
                type="submit" 
                style={isButtonHovered ? buttonHoverStyle : pageStyles.button}
                onMouseEnter={() => setIsButtonHovered(true)}
                onMouseLeave={() => setIsButtonHovered(false)}
              >
                Update Score
              </button>
            </form>
          </>
        )}
        
        {/* Game List Section */}
        <div style={pageStyles.gameListSection}>
          <h3 style={pageStyles.gameListTitle}>Recent Games</h3>
          
          {games.length === 0 ? (
            <div style={pageStyles.infoText}>No games found for this team.</div>
          ) : (
            <ul style={pageStyles.gameList}>
              {games.map((game) => {
                const scoreDisplay = getScoreDisplay(game);
                
                return (
                  <li key={game.id} style={pageStyles.gameItem}>
                    <div>
                      <div style={pageStyles.gameDateAndOpponent}>
                        {formatGameDate(game.date)} vs {game.opponentName || "Opponent"}
                      </div>
                      <div style={{...pageStyles.gameScore, marginTop: "4px"}}>
                        {scoreDisplay.hasScore ? (
                          <span>
                            Your Score: <strong>{scoreDisplay.yourScore}</strong> vs 
                            Opponent Score: <strong>{scoreDisplay.opponentScore}</strong>
                          </span>
                        ) : (
                          "No score recorded"
                        )}
                      </div>
                    </div>
                    <div 
                      style={{
                        ...pageStyles.statusBadge, 
                        ...(game.status === "completed" ? pageStyles.statusCompleted : pageStyles.statusPending)
                      }}
                    >
                      {game.status === "completed" ? "Final" : "Upcoming"}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

export default GameStatsPage;